import React, { useMemo } from 'react';
import {
  useFeaturesByModelGraphQuery,
  useFeaturesByAssetGraphQuery,
} from 'queries/features/queries';
import {
  LineChart,
  useChartVariables,
  ChartFilters,
  ChartLegend,
} from '@shape/lighthouse-components';
import useFeaturesTags from './hooks/useFeatureTags';
import { buildTagsConfig } from '../helpers/helper';
import { useInterpolationData } from '../hooks/useInterpolationData';

const FeaturesCharts: React.FC<
  DefaultChartComponentProps & {
    modelName?: string;
  }
> = ({
  assetId,
  showExcludeGap,
  children,
  enableCustomRange,
  hasLegendCategoryFilter,
  overrideGraphicOptions,
  modelName,
  showDefaultLegend = true,
  alarmsInfo,
  customPeriodOptions,
}) => {
  const { dateRange, selectedChartVariables } = useChartVariables();
  const { tags, isLoading } = useFeaturesTags(assetId, modelName);

  const window = useInterpolationData(dateRange);
  const featureChartQuery = modelName ? useFeaturesByModelGraphQuery : useFeaturesByAssetGraphQuery;
  const tagsConfig = useMemo(() => buildTagsConfig(tags), [tags, modelName, assetId]);
  const { data, isFetching, isError } = featureChartQuery(
    {
      assetId,
      model_name: modelName,
      startDate: dateRange.start,
      endDate: dateRange.end,
      tags: selectedChartVariables.filter((tag) => tagsConfig.has(tag)).sort(),
      window,
    },
    {
      enabled:
        (!!modelName || !!assetId) &&
        !!window &&
        selectedChartVariables.filter((tag) => tagsConfig.has(tag)).length > 0,
    },
  );

  return (
    <>
      <ChartFilters
        showExcludeGap={showExcludeGap}
        enableCustomRange={enableCustomRange}
        hasAlarmPeriod={!showExcludeGap}
        hasLegendCategory={hasLegendCategoryFilter}
        tags={{ ALL: tags }}
        modelName={modelName}
        customPeriodOptions={customPeriodOptions}
      />

      {children}
      <LineChart
        chartData={
          data?.features?.filter((feature) => selectedChartVariables.indexOf(feature.tag) !== -1) ??
          []
        }
        tagsConfig={tagsConfig}
        isFetching={isFetching || isLoading}
        isError={isError}
        overrideGraphicOptions={overrideGraphicOptions}
        alarmsInfo={alarmsInfo}
      />

      {showDefaultLegend && <ChartLegend hasLegendCategoryFilter />}
    </>
  );
};

export default FeaturesCharts;
